import { AsyncPipe, NgFor } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOption, MatOptionModule } from '@angular/material/core';
import { MatInput, MatInputModule } from '@angular/material/input';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, switchMap } from 'rxjs/operators';

@Component({
    selector: 'formly-autocomplete-type',
    template: `
		<input
			matInput
			[matAutocomplete]="auto"
			[formControl]="formControl"
			[formlyAttributes]="field"
			[placeholder]="props.placeholder"
			[readonly]="props.readonly"
			[errorStateMatcher]="errorStateMatcher"
			(blur)="onBlur()"
			(keydown)="onKeyDown($event)"
		/>
		<mat-autocomplete #auto="matAutocomplete" (optionSelected)="onOptionChanged($event.option)">
			<mat-option *ngFor="let option of filter | async" [value]="option" [id]="displayFn(option)">
				<span>
					{{ displayFn(option) }}
				</span>
			</mat-option>
		</mat-autocomplete>
	`,
    styles: ['span {white-space: normal;}'],
    standalone: true,
    imports: [
        MatInputModule,
        MatAutocompleteModule,
        FormsModule,
        ReactiveFormsModule,
        FormlyModule,
        NgFor,
        MatOptionModule,
        AsyncPipe,
    ],
})
export class FormlyAutocompleteTypeComponent extends FieldType<FormlyFieldConfig> implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild(MatInput) formFieldControl: MatInput;
	@ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

	filter: Observable<any>;
	selectedId: string;

	public ngOnInit(): void {
		this.filter = this.formControl.valueChanges.pipe(
			startWith(''),
			distinctUntilChanged(),
			debounceTime(300),
			switchMap((search) => {
				// TODO: investigate why do we put an object into the select or autocomplete since this doesn't make sense and completely wrong
				if (typeof search !== 'object') {
					return this.props.filter(search, this.formControl.value);
				}
				return of([]);
			})
		);

		if (this.props.selectedItem) {
			this.selectedId = this.props.selectedItem;
		}

		window.addEventListener('scroll', this.scrollEvent, true);
	}

	public ngOnDestroy(): void {
		super.ngOnDestroy();
		window.removeEventListener('scroll', this.scrollEvent, true);
	}

	public ngAfterViewInit(): void {
		// temporary fix for https://github.com/angular/material2/issues/6728
		// eslint-disable-next-line no-underscore-dangle
		(this.autocomplete as any)._formField = this.formField;
		if (this.props.dropCustomValues) {
			//This shouldn't always be used and is more situational for Assigned Employees.
			//TODO: Handle setting the value and id properties correctly
			this.autocomplete.autocomplete.displayWith = this.displayFn.bind(this);
		}
	}

	// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
	public displayFn(value: any): any {
		if (!value) {
			return null;
		}

		return this.props.filterDisplayValueFunc ? this.props.filterDisplayValueFunc(value) : value;
	}

	public onBlur(): void {
		this.checkSelectedOption();
	}

	public onKeyDown(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			this.checkSelectedOption();
		}
	}

	public onOptionChanged(selectedOption: MatOption): void {
		this.selectedId = selectedOption.id;

		if (this.props.onOptionChanged) {
			this.props.onOptionChanged(selectedOption.value);
		}
	}

	public filtersTrackByFn(index: number, option: unknown): any {
		return this.displayFn(option) ?? index;
	}

	private checkSelectedOption(): void {
		if (!this.props.dropCustomValues || this.props.readonly) {
			return;
		}

		if (!this.selectedId && this.formFieldControl.value) {
			this.formControl.setValue('', { emitEvent: false });
		}

		if (this.selectedId && this.formFieldControl.value && this.selectedId !== this.formFieldControl.value) {
			this.selectedId = null;
			this.formControl.setValue('', { emitEvent: false });
		}
	}

	private readonly scrollEvent = (): void => {
		if (this.autocomplete?.panelOpen) {
			this.autocomplete?.updatePosition();
		}
	};
}
